interface PricingCardsProps {
  onSubmitFreeClick: () => void;
}

interface PlanFeature {
  text: string;
}

interface Plan {
  id: string;
  name: string;
  description: string;
  price: string;
  priceSub: string;
  badge?: string;
  isFeatured: boolean;
  features: PlanFeature[];
  buttonText: string;
  buttonHref?: string;
  isFormButton?: boolean;
  buttonIcon: string;
}

const plans: Plan[] = [
  {
    id: 'free',
    name: 'Basic Listing',
    description: 'Get your tool listed and discovered',
    price: '$0',
    priceSub: 'forever free',
    isFeatured: false,
    features: [
      { text: 'Standard listing' },
      { text: 'Category placement' },
      { text: 'Tool description page' },
    ],
    buttonText: 'Submit for Free',
    isFormButton: true,
    buttonIcon: 'ri-send-plane-line',
  },
  {
    id: 'featured',
    name: 'Featured Listing',
    description: 'Stand out from the crowd',
    price: '$39',
    priceSub: 'one-time payment',
    badge: 'Most Popular',
    isFeatured: true,
    features: [
      { text: 'Everything in Basic' },
      { text: 'Featured badge' },
      { text: 'Priority placement in category' },
      { text: 'Bold listing title' },
    ],
    buttonText: 'Get Featured',
    buttonHref: 'https://buy.stripe.com/7sYfZg3Xkehv9jz2DM9k400',
    buttonIcon: 'ri-star-line',
  },
  {
    id: 'premium',
    name: 'Premium Listing',
    description: 'Maximum visibility for your tool',
    price: '$99',
    priceSub: 'one-time payment',
    isFeatured: false,
    features: [
      { text: 'Everything in Featured' },
      { text: 'Homepage spotlight' },
      { text: 'Newsletter mention' },
      { text: 'Social media shoutout @toolnovaHQ on X' },
    ],
    buttonText: 'Get Premium',
    buttonHref: 'https://buy.stripe.com/fZu8wOalIflzgM17Y69k401',
    buttonIcon: 'ri-trophy-line',
  },
];

const trustSignals = [
  { icon: 'ri-shield-check-line', text: 'One-time payment — no subscriptions, no hidden fees' },
  { icon: 'ri-time-line', text: 'Listed within 48 hours of submission' },
  { icon: 'ri-mail-line', text: 'Questions? Reach us at toolnova.hq@gmail.com' },
];

export default function PricingCards({ onSubmitFreeClick }: PricingCardsProps) {
  return (
    <section className="px-4 sm:px-6 lg:px-8 pt-28 pb-12">
      <div className="max-w-6xl mx-auto">

        {/* Section Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-teal-500/10 border border-teal-500/25 rounded-full mb-6">
            <i className="ri-rocket-line text-teal-400 text-sm"></i>
            <span className="text-xs font-semibold text-teal-400 uppercase tracking-widest">List Your Tool</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold mb-4 bg-gradient-to-br from-white via-gray-100 to-gray-400 bg-clip-text text-transparent">
            Choose How to List Your Tool
          </h1>
          <p className="text-base sm:text-lg text-gray-400 max-w-xl mx-auto leading-relaxed">
            Join 150+ tools already listed on ToolNova. Get discovered by thousands of AI enthusiasts.
          </p>
        </div>

        {/* Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-start mb-10">
          {plans.map((plan) => (
            <div
              key={plan.id}
              className={`relative rounded-2xl border flex flex-col transition-all duration-300 ${
                plan.isFeatured
                  ? 'bg-gray-900/70 border-teal-500/50 ring-1 ring-teal-500/30'
                  : 'bg-gray-900/40 border-gray-800/60 hover:border-gray-700/80'
              }`}
            >
              {/* Most Popular Badge */}
              {plan.badge && (
                <div className="absolute -top-3.5 left-1/2 -translate-x-1/2">
                  <span className="inline-flex items-center gap-1 px-4 py-1 bg-teal-500 text-white text-xs font-bold rounded-full whitespace-nowrap shadow-lg shadow-teal-500/30">
                    <i className="ri-fire-line text-xs"></i>
                    {plan.badge}
                  </span>
                </div>
              )}

              <div className="p-8 flex flex-col flex-1">

                {/* Plan Name + Description */}
                <div className="mb-5">
                  <h3 className="text-lg font-bold text-white mb-1">{plan.name}</h3>
                  <p className="text-sm text-gray-500">{plan.description}</p>
                </div>

                {/* Price */}
                <div className="mb-8">
                  <div className="flex items-end gap-2">
                    <span className={`text-5xl font-extrabold ${plan.isFeatured ? 'text-teal-400' : 'text-white'}`}>
                      {plan.price}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1 uppercase tracking-wider">{plan.priceSub}</p>
                </div>

                {/* Divider */}
                <div className={`h-px mb-7 ${plan.isFeatured ? 'bg-teal-500/20' : 'bg-gray-800/60'}`}></div>

                {/* Features */}
                <ul className="space-y-4 mb-10 flex-1">
                  {plan.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <div className="w-5 h-5 flex items-center justify-center shrink-0 mt-0.5">
                        <i className={`ri-check-line text-sm font-bold ${plan.isFeatured ? 'text-teal-400' : 'text-teal-500/70'}`}></i>
                      </div>
                      <span className="text-sm text-gray-300 leading-snug">{feature.text}</span>
                    </li>
                  ))}
                </ul>

                {/* CTA Button */}
                {plan.isFormButton ? (
                  <button
                    onClick={onSubmitFreeClick}
                    className="w-full flex items-center justify-center gap-2 px-6 py-3.5 bg-gray-800/80 hover:bg-gray-700/80 border border-gray-700/60 hover:border-gray-600 text-white text-sm font-semibold rounded-xl transition-all duration-300 whitespace-nowrap cursor-pointer hover:scale-[1.02] active:scale-[0.98]"
                  >
                    <i className={`${plan.buttonIcon} text-base`}></i>
                    {plan.buttonText}
                  </button>
                ) : (
                  <a
                    href={plan.buttonHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`w-full flex items-center justify-center gap-2 px-6 py-3.5 text-sm font-semibold rounded-xl transition-all duration-300 whitespace-nowrap cursor-pointer hover:scale-[1.02] active:scale-[0.98] ${
                      plan.isFeatured
                        ? 'bg-teal-500 hover:bg-teal-400 text-white shadow-lg shadow-teal-500/25 hover:shadow-teal-400/40'
                        : 'bg-gray-800/80 hover:bg-gray-700/80 border border-gray-700/60 hover:border-teal-500/40 text-white'
                    }`}
                  >
                    <i className={`${plan.buttonIcon} text-base`}></i>
                    {plan.buttonText}
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Trust Signals */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-8 pt-4 pb-2">
          {trustSignals.map((signal, idx) => (
            <div key={idx} className="flex items-center gap-2 text-sm text-gray-400">
              <i className={`${signal.icon} text-teal-400 text-base shrink-0`}></i>
              <span>{signal.text}</span>
            </div>
          ))}
        </div>

      </div>
    </section>
  );
}
